import React from "react";
import { motion } from "framer-motion";
import {
  FaCloud,
  FaShieldAlt,
  FaBook,
  FaLightbulb,
  FaAward,
  FaCode,
} from "react-icons/fa";
import { HiOutlineExternalLink } from "react-icons/hi";

const AboutSection = () => {
  const expertise = [
    {
      icon: FaShieldAlt,
      title: "Security Enthusiast",
      description:
        "Creator of VXDF - working to standardize vulnerability data exchange across the cybersecurity industry",
      highlight: "VXDF Creator",
      color: "from-vxdf-primary to-vxdf-secondary",
    },
    {
      icon: FaCloud,
      title: "Cloud Security Leader",
      description:
        "Building secure cloud infrastructures and helping organizations strengthen their security posture",
      highlight: "10+ Years Experience",
      color: "from-blue-500 to-cyan-500",
    },
    {
      icon: FaBook,
      title: "Technical Author",
      description:
        "Author of 'Cloud Native Software Security Handbook' - sharing practical security knowledge",
      highlight: "Published Author",
      color: "from-purple-500 to-pink-500",
    },
  ];

  const achievements = [
    { icon: FaAward, value: "50+", label: "Security Projects" },
    { icon: FaCode, value: "100K+", label: "Lines of Code" },
    { icon: FaLightbulb, value: "20+", label: "Organizations Helped" },
  ];

  return (
    <motion.section
      id="about"
      className="py-24 bg-gradient-to-b from-vxdf-dark via-gray-900 to-black relative overflow-hidden"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true }}
    >
      {/* Background decoration with dark theme */}
      <div className="absolute inset-0 bg-hero-pattern opacity-5"></div>
      <motion.div
        className="absolute top-0 right-0 w-96 h-96 bg-vxdf-accent/5 rounded-full blur-3xl"
        animate={{ scale: [1, 1.1, 1], rotate: [0, 45, 0] }}
        transition={{ duration: 15, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-0 left-0 w-96 h-96 bg-vxdf-secondary/5 rounded-full blur-3xl"
        animate={{ scale: [1.1, 1, 1.1], rotate: [45, 0, 45] }}
        transition={{ duration: 20, repeat: Infinity }}
      />

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-responsive-4xl font-black mb-6">
            <span className="text-gray-100">Building </span>
            <span className="gradient-text">Secure Solutions</span>
            <span className="text-gray-100"> for Tomorrow</span>
          </h2>
          <p className="text-responsive-lg text-gray-400 max-w-3xl mx-auto">
            Passionate about cybersecurity and dedicated to making the digital
            world safer through open-source contributions and practical security
            solutions.
          </p>
        </motion.div>

        {/* Expertise Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {expertise.map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
              className="group relative"
            >
              <div
                className="absolute inset-0 bg-gradient-to-r opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl blur-xl"
                style={{
                  backgroundImage: `linear-gradient(135deg, ${item.color})`,
                }}
              />
              <div className="relative bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 h-full border border-gray-700/50 hover:border-vxdf-accent/50">
                {/* Icon with gradient background */}
                <div
                  className={`w-16 h-16 rounded-xl bg-gradient-to-r ${item.color} flex items-center justify-center mb-6 shadow-lg`}
                >
                  <item.icon className="text-2xl text-white" />
                </div>

                {/* Badge */}
                <span className="inline-block px-3 py-1 bg-gray-700/50 text-gray-300 text-xs font-semibold rounded-full mb-4">
                  {item.highlight}
                </span>

                <h3 className="text-xl font-bold text-gray-100 mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {item.description}
                </p>

                {item.title === "Security Enthusiast" && (
                  <motion.a
                    href="https://vxdf.org"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center mt-4 text-vxdf-accent hover:text-vxdf-secondary font-semibold transition-colors"
                    whileHover={{ x: 5 }}
                  >
                    Learn about VXDF
                    <HiOutlineExternalLink className="ml-1" />
                  </motion.a>
                )}

                {item.title === "Technical Author" && (
                  <motion.a
                    href="/book"
                    className="inline-flex items-center mt-4 text-purple-400 hover:text-purple-300 font-semibold transition-colors"
                    whileHover={{ x: 5 }}
                  >
                    Explore My Book
                    <HiOutlineExternalLink className="ml-1" />
                  </motion.a>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Achievement Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-vxdf-primary/20 to-vxdf-secondary/20 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-vxdf-accent/20"
        >
          <div className="grid grid-cols-3 gap-8">
            {achievements.map((achievement, index) => (
              <motion.div
                key={achievement.label}
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                transition={{ delay: index * 0.1, type: "spring" }}
                viewport={{ once: true }}
                className="text-center text-white"
              >
                <achievement.icon className="text-3xl mb-3 mx-auto opacity-80" />
                <div className="text-3xl font-black mb-1">
                  {achievement.value}
                </div>
                <div className="text-sm opacity-90">{achievement.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <p className="text-gray-400 mb-6">
            Interested in collaborating on security projects?
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <motion.a
              href="/contact"
              className="btn-primary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Get in Touch
            </motion.a>
            <motion.a
              href="/testimonials"
              className="btn-secondary bg-gray-800 text-white border-gray-600 hover:bg-gray-700"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Read Testimonials
            </motion.a>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default AboutSection;
